// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, Trash2, Megaphone, CheckCircle2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const CHANNELS = ["email", "sms", "whatsapp", "push"];
const SEGMENTS = ["customers", "leads", "b2b"];

export default function Campaigns() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "email", subject: "", body: "", audience: { segment: "customers" } });

  const load = useCallback(async () => {
    const r = await api.get("/crm/campaigns");
    setRows(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.post("/crm/campaigns", form);
      toast.success("Campaign drafted");
      setOpen(false);
      setForm({ name: "", channel: "email", subject: "", body: "", audience: { segment: "customers" } });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const send = async (c) => {
    if (!window.confirm(`Send this campaign to all ${c.audience?.segment || "customers"}? Emails are logged to notifications until Resend is wired up.`)) return;
    const r = await api.post(`/crm/campaigns/${c.id}/send`);
    toast.success(`Sent to ${r.data.sent_count} recipients`);
    load();
  };
  const del = async (c) => {
    if (!window.confirm("Delete campaign?")) return;
    await api.delete(`/crm/campaigns/${c.id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2"><Megaphone className="w-5 h-5" /> Campaigns</h2>
          <div className="text-xs text-muted-foreground">Mocked send until Resend/Twilio are wired. Notifications go to the recipient inbox.</div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="crm-new-campaign-btn">
          <Plus className="w-4 h-4 mr-2" /> New campaign
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(c => (
          <div key={c.id} data-testid={`campaign-card-${c.id}`} className="rounded-xl border border-border bg-card p-4 space-y-2">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground uppercase">{c.channel} · {c.audience?.segment}</div>
              </div>
              <Badge variant={c.status === "sent" ? "default" : "secondary"}>{c.status}</Badge>
            </div>
            {c.subject && <div className="text-sm"><b>Subject:</b> {c.subject}</div>}
            <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-24 overflow-y-auto">{c.body}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Sent to {c.sent_count}
              {c.sent_at && <> · {new Date(c.sent_at).toLocaleString()}</>}
            </div>
            <div className="flex gap-2 pt-1">
              {c.status !== "sent" && (
                <Button size="sm" onClick={() => send(c)} data-testid={`campaign-send-${c.id}`}>
                  <Send className="w-3.5 h-3.5 mr-2" /> Send now
                </Button>
              )}
              {c.status === "sent" && (
                <Badge variant="outline" className="text-emerald-600 border-emerald-600/40">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Delivered
                </Badge>
              )}
              <Button size="sm" variant="ghost" onClick={() => del(c)} data-testid={`campaign-delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-2 text-center text-sm text-muted-foreground py-10">
            No campaigns yet — create your first one.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New campaign</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="campaign-form-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Channel</Label>
                <Select value={form.channel} onValueChange={v => setForm({...form, channel: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Audience</Label>
                <Select value={form.audience.segment} onValueChange={v => setForm({...form, audience: { segment: v }})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SEGMENTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.channel === "email" && (
              <div><Label className="text-xs">Subject</Label><Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} /></div>
            )}
            <div><Label className="text-xs">Body</Label>
              <Textarea rows={5} value={form.body} onChange={e => setForm({...form, body: e.target.value})} placeholder="Use {{name}} to personalize." data-testid="campaign-form-body" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="campaign-form-save">Save draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
